// eslint-disable-next-line no-restricted-imports
import { drawDOM } from "@progress/kendo-drawing";
// eslint-disable-next-line no-restricted-imports
import { toBlob } from "@progress/kendo-drawing/pdf";
// eslint-disable-next-line no-restricted-imports
import { PDFExport } from "@progress/kendo-react-pdf";
import { useCallback, useRef, useState } from "react";

import { cn } from "@btnet/utilities-core";

import { BdsActionBar, BdsButtonGroup, BdsRollupButton, BdsText } from "@buildertrend/components";

import { type IExportHeader } from "helpers/routing/EntityLinkRouter/EntityLinkRouter.api.types";
import { isWebview } from "helpers/webview/utilities";

import { isNullOrUndefined } from "utilities/object/object";

import { BTAlert } from "commonComponents/btWrappers/BTAlert/BTAlert";
import { BTModal, type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTModalLayoutBDS } from "commonComponents/btWrappers/BTModalBDS/BTModalLayoutBDS";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import { BTQRCode, type IBTQRCodeRef } from "commonComponents/utilities/BTQRCode/BTQRCode";
import { Thumbnail } from "commonComponents/utilities/Thumbnail/Thumbnail";

import { type ExportHeaderResponse } from "handlers/models";

import "./GenerateQRCodeModal.less";

export interface IGenerateQRCodeModalProps {
    readonly modalConfig?: IModalConfiguration;
    readonly shareUrl?: string | null;
    readonly title: string;
    readonly exportHeader?: ExportHeaderResponse | IExportHeader;
    /** Optional alert text (for BTFolderQRCode alerts) */
    readonly alertText?: string;
    /** Optional custom description content */
    readonly bottomDescriptionMarkup?: React.ReactNode;
}

export default function GenerateQRCodeModal({
    modalConfig,
    shareUrl,
    title,
    exportHeader,
    alertText,
    bottomDescriptionMarkup,
}: IGenerateQRCodeModalProps) {
    const pdfRef = useRef<PDFExport>(null);
    const qrCodeRef = useRef<IBTQRCodeRef>(null);
    const pdfContentRef = useRef<HTMLDivElement>(null);
    const [isExporting, setIsExporting] = useState(false);

    const runPdfExport = useCallback(() => {
        const fileName = isNullOrUndefined(title) ? "QR.pdf" : `${title}_QR.pdf`;

        if (isWebview(window) && window.onDownloadFile && pdfContentRef.current) {
            const { onDownloadFile } = window;
            setIsExporting(true);
            // Use drawDOM directly (the same underlying call PDFExport.save() makes internally)
            // so the webview PDF is identical to the browser PDF. Options are read from pdfRef
            // so any changes to the PDFExport props above are automatically picked up here.
            const { paperSize, margin, scale, keepTogether } = pdfRef.current!.props;
            void drawDOM(pdfContentRef.current, {
                paperSize,
                margin: margin as {
                    top?: number | string;
                    left?: number | string;
                    right?: number | string;
                    bottom?: number | string;
                },
                scale,
                keepTogether,
            }).then((group) => {
                toBlob(group, (blob) => {
                    onDownloadFile({ blob, filename: fileName });
                    setIsExporting(false);
                });
            });
            return;
        }

        setIsExporting(true);
        pdfRef.current!.save(() => {
            setIsExporting(false);
        });
    }, [title]);

    if (!shareUrl) {
        return <BTLoading />;
    }

    const component = (
        <BTModalLayoutBDS
            title="Generate QR Code"
            className="GenerateQRCodeModalLayout"
            modalConfig={modalConfig}
            footerContent={
                <BdsActionBar testid="qrCodeActions" id="qrCodeActions">
                    <BdsButtonGroup
                        testid="qrCodeActionsGroup"
                        id="qrCodeActionsGroup"
                        flexAlign="end"
                        grow
                    >
                        <BdsRollupButton
                            value="Open link"
                            displayType="secondary"
                            text="Open link"
                            testid="QRCodeRawLink"
                            id="QRCodeRawLink"
                            onClick={() => window.open(shareUrl, "_blank", "noopener noreferrer")}
                        />
                        <BdsRollupButton
                            displayType="secondary"
                            text="Download QR code"
                            value="Download QR code"
                            testid="QRCodeDownload"
                            id="QRCodeDownload"
                            onClick={() => qrCodeRef.current?.download(`${title}_QR_Code`)}
                        />
                        <BdsRollupButton
                            displayType="secondary"
                            text="Cancel"
                            value="cancel"
                            testid="cancelQRCode"
                            id="cancelQRCode"
                            onClick={modalConfig?.beforeClose}
                            disabled={isExporting}
                        />
                        <BdsRollupButton
                            displayType="primary"
                            text="Download printout"
                            value="Download printout"
                            testid="downloadQrPdfLink"
                            id="downloadQrPdfLink"
                            onClick={runPdfExport}
                        />
                    </BdsButtonGroup>
                </BdsActionBar>
            }
        >
            <div className="GenerateQRCodeModalPreview">
                {alertText && (
                    <BTAlert
                        className="margin-bottom-xs"
                        data-testid="qrCodeBanner"
                        showIcon
                        type="info"
                        message={alertText}
                    />
                )}
                <BdsText
                    className={cn("margin-bottom-xs", { "margin-top-lg": !alertText })}
                    size="normal-lg"
                >
                    Preview of 8.5" x 11"
                </BdsText>
                <div ref={pdfContentRef} className="GenerateQRCodeModalContainer">
                    <PDFExport
                        ref={pdfRef}
                        fileName={isNullOrUndefined(title) ? "QR.pdf" : title + "_QR.pdf"}
                        author="Buildertrend"
                        margin={{ top: 10, left: 20, right: 20, bottom: 10 }}
                        scale={1.4}
                        paperSize="Letter"
                        keepTogether="GenerateQRCodeModalContent"
                    >
                        <div className="GenerateQRCodeModalContent">
                            {exportHeader && (
                                <>
                                    <Thumbnail
                                        className="QRCodeLogoThumbnail"
                                        lazyLoad={false}
                                        src={exportHeader.logoPath}
                                        height={500}
                                        width={500}
                                        alt="Company logo"
                                    />
                                    {exportHeader.jobName && (
                                        <BdsText
                                            className="GenerateQRCodeModalText JobName margin-top-md margin-bottom-md"
                                            size="normal-md"
                                        >
                                            {exportHeader.jobName}
                                        </BdsText>
                                    )}
                                </>
                            )}
                            {!isNullOrUndefined(title) && (
                                <BdsText
                                    className="GenerateQRCodeModalText BoldText"
                                    size="distinct-md"
                                >
                                    {title}
                                </BdsText>
                            )}

                            {shareUrl && (
                                <BTQRCode
                                    ref={qrCodeRef}
                                    value={shareUrl}
                                    roundedCorners
                                    testId="qrcode"
                                    className="GenerateQRCodeModalQRCode"
                                />
                            )}

                            <BdsText
                                className="qr-code-description"
                                text={bottomDescriptionMarkup}
                            />
                        </div>
                    </PDFExport>
                </div>
            </div>
        </BTModalLayoutBDS>
    );

    if (modalConfig) {
        return (
            <BTModal
                visible
                removeBodyPadding
                width="560px"
                title="Generate QR Code"
                useModalLayout
                beforeClose={modalConfig.beforeClose}
            >
                {component}
            </BTModal>
        );
    }

    return component;
}
